/** The workspace header: which graph, what state it is in, and the two actions. */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Modal } from '@/shared/components'
import { useToast } from '@/shared/components/toast'
import { Chip } from '../components'
import { GenerateDialog } from '../generate/GenerateDialog'
import { GraphSwitcher } from './GraphSwitcher'
import { PipelineBadge } from './PipelineBadge'
import { usePublishGraph, useSourceMutations } from '../hooks'
import type { Graph } from '../types/brain.types'

export function BrainTopBar({
  graphs,
  graphId,
  onSwitch,
}: {
  graphs: Graph[]
  graphId: string
  onSwitch: (id: string) => void
}) {
  const graph = graphs.find((g) => g.graph_id === graphId)
  const navigate = useNavigate()
  const [publishing, setPublishing] = useState(false)
  const [generating, setGenerating] = useState(false)

  return (
    <>
      <div className="graph-page-heading">
        <div>
          <h1>{graph?.name ?? 'Knowledge graph'}</h1>
          <p>{graph?.description || 'Explore the model, sources and records in your knowledge graph.'}</p>
        </div>
      </div>
      <div className="cp-topbar">
        <button
          className="cp-iconbtn"
          onClick={() => navigate('/brain')}
          title="All knowledge graphs"
          aria-label="Back to knowledge graphs"
        >
          ←
        </button>

        <div className="cp-crumb">
          <span className="hide-narrow">Knowledge graphs</span>
          <span className="faint hide-narrow">/</span>
          <GraphSwitcher graphs={graphs} graphId={graphId} onSwitch={onSwitch} />
        </div>

        {graph?.published_version ? (
          <Chip tone="live" dot>
            Live · {graph.published_version}
          </Chip>
        ) : (
          <Chip>Draft</Chip>
        )}

        <PipelineBadge graphId={graphId} />

        <span className="cp-spacer" />
        <button className="btn btn-sm" onClick={() => setGenerating(true)}>
          Generate
        </button>
        <button className="btn btn-primary btn-sm" onClick={() => setPublishing(true)}>
          Publish
        </button>
      </div>

      {publishing && (
        <PublishDialog
          graphId={graphId}
          current={graph?.published_version ?? null}
          onClose={() => setPublishing(false)}
        />
      )}
      {generating && (
        <GenerateDialog
          graphId={graphId}
          onClose={() => setGenerating(false)}
          onDone={(id) => {
            setGenerating(false)
            if (id !== graphId) onSwitch(id)
          }}
        />
      )}
    </>
  )
}

function nextVersion(current: string | null): string {
  const match = /^v?(\d+)\.(\d+)$/.exec(current ?? '')
  if (!match) return 'v1.0'
  return `v${match[1]}.${Number(match[2]) + 1}`
}

function PublishDialog({
  graphId,
  current,
  onClose,
}: {
  graphId: string
  current: string | null
  onClose: () => void
}) {
  const publish = usePublishGraph(graphId)
  const { publishAll } = useSourceMutations(graphId)
  const toast = useToast()
  const [version, setVersion] = useState(() => nextVersion(current))
  const [note, setNote] = useState('')

  return (
    <Modal
      title="Publish this graph"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={publish.isPending || !version.trim()}
            onClick={async () => {
              try {
                // Two things, in the order that makes them safe: stamp the
                // model first, so the records that arrive are attributable to
                // a version, then fetch from every source and run the rules.
                await publish.mutateAsync({
                  version: version.trim(),
                  note: note.trim() || undefined,
                })
                const outcome = await publishAll.mutateAsync()
                const buckets = (outcome.counts?.buckets ?? {}) as Record<string, number>

                if (outcome.failed.length) {
                  toast.error(
                    new Error(
                      outcome.failed
                        .map((f) => `${f.source_id}: ${f.error ?? 'failed'}`)
                        .join('; '),
                    ),
                    `Published ${version.trim()} — ${outcome.published} of ${
                      outcome.sources.length
                    } sources fetched`,
                  )
                } else if (outcome.sources.length === 0) {
                  toast.success(
                    `Published ${version.trim()}`,
                    'The model is stamped. Connect a source to bring records in.',
                  )
                } else {
                  toast.success(
                    `Published ${version.trim()}`,
                    `${outcome.staged} fetched from ${outcome.published} source${
                      outcome.published === 1 ? '' : 's'
                    } · ${buckets.rejected ?? 0} rejected · ${
                      buckets.potential ?? 0
                    } waiting for review`,
                  )
                }
                onClose()
              } catch (error) {
                toast.error(error)
              }
            }}
          >
            {publish.isPending || publishAll.isPending ? 'Publishing…' : 'Publish'}
          </button>
        </>
      }
    >
      <div className="stack">
        <p className="muted small" style={{ margin: 0 }}>
          Publishing stamps the model as it stands right now, so anything reading this graph can
          pin to a version. It does not stop you editing — the next edit simply belongs to the
          next version.
        </p>
        <div className="field">
          <label className="field-label" htmlFor="pub-version">
            Version
          </label>
          <input
            id="pub-version"
            className="input"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
          />
          {current && <p className="field-help">Currently published: {current}</p>}
        </div>
        <div className="field">
          <label className="field-label" htmlFor="pub-note">
            What changed
          </label>
          <textarea
            id="pub-note"
            className="textarea"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Added Risk, and a match rule for Account on domain."
          />
        </div>
      </div>
    </Modal>
  )
}
